import { Box, useTheme } from '@mui/material'
import { GridColumns } from '@mui/x-data-grid-pro'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CommonPageLayout } from 'src/components/common/CommonPageLayout'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { APIResultCount } from 'src/constant'
import { useNavigation } from 'src/context/NavigationContext'
import { AppRoutesNavigation } from '../../../../app-routes'
import { timeReportsEndpoints, useGetFilterTimeReport } from '../../../../api/time-report'
import GenericTable from '../../../../components/table/GenericTable'
import { useCustomerCtx } from '../../../../hooks/context-hooks'
import {
  DateTimeStackCell,
  DurationCell,
  MultilineCell,
  PlacementCell,
  diffMinutes,
  formatDuration,
  formatHoursMinutes,
} from '../../../localities/time-reports/TimeReportCells'
import { TotalTimeBox } from '../../../localities/time-reports/TimeReportsContainer'
import TimeReportFilter from './TimeReportsFilter'

const TimeReports = () => {
  const { t } = useTranslation(['time-reports', 'common', 'task'])
  const [queryParams, setQueryParams] = useState({})
  const { customer } = useCustomerCtx()
  const theme = useTheme()
  const { navigateTo } = useNavigation()

  const { data: summaryData } = useGetFilterTimeReport(customer.id, 0, APIResultCount.Max, queryParams)
  const summary = useMemo(() => {
    const items = summaryData?.items ?? []
    const totalMinutes = items.reduce((sum, r) => sum + diffMinutes(r.fromDateTime, r.toDateTime), 0)
    const uniqueResources = new Set(items.map(r => r.resourceId).filter(Boolean)).size
    return {
      count: items.length,
      totalTime: formatHoursMinutes(totalMinutes),
      uniqueResources,
    }
  }, [summaryData])

  const timeReportFilterChange = useCallback(params => {
    params.resourceIds = params.resource.map(re => re.id).join(',')
    params.localityIds = params.locality.map(re => re.id).join(',')
    params.timeReportTypeIds = params.timeReportType.map(re => re.id).join(',')
    delete params.locality
    delete params.resource
    delete params.timeReportType
    setQueryParams(params)
  }, [])

  const STATIC_COLUMNS: GridColumns = useMemo(
    () => [
      {
        field: 'description',
        headerName: t('description', { ns: 'common' }),
        flex: 1.5,
        cellClassName: 'font-weight-semi-bold',
        renderCell: ({ row }) => <MultilineCell value={row?.description} maxLines={2} />,
      },
      {
        field: 'placement',
        headerName: t('placement'),
        flex: 1,
        sortable: false,
        valueGetter: ({ row }) => [row?.localityName, row?.taskTitle].filter(Boolean).join(' → '),
        renderCell: ({ row }) => {
          if (!row?.localityId || !row?.scheduledTaskId) {
            return <PlacementCell localityName={row?.localityName} taskTitle={row?.taskTitle} />
          }
          return (
            <Box
              onClick={e => {
                e.stopPropagation()
                navigateTo(AppRoutesNavigation.Localities.workOrder(customer.id, row.localityId, row.scheduledTaskId))
              }}
              sx={{
                display: 'block',
                width: '100%',
                color: theme => getThemeColor(theme, MUIThemeColors.primaryMain),
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              <PlacementCell localityName={row?.localityName} taskTitle={row?.taskTitle} />
            </Box>
          )
        },
      },
      {
        field: 'fromDateTime',
        headerName: t('fromDateAndTime'),
        flex: 1,
        renderCell: ({ row }) => <DateTimeStackCell value={row?.fromDateTime} />,
      },
      {
        field: 'toDateTime',
        headerName: t('toDateAndTime'),
        flex: 1,
        renderCell: ({ row }) => <DateTimeStackCell value={row?.toDateTime} />,
      },
      {
        field: 'duration',
        headerName: t('duration'),
        flex: 0.7,
        sortable: false,
        valueGetter: ({ row }) => formatDuration(row?.fromDateTime, row?.toDateTime),
        renderCell: ({ row }) => <DurationCell from={row?.fromDateTime} to={row?.toDateTime} />,
      },
      {
        field: 'timeReportTypeName',
        headerName: t('timeReportType'),
        flex: 1,
        renderCell: row => row.row.timeReportTypeName,
      },
      {
        field: 'resourceName',
        headerName: t('resource', { ns: 'task' }),
        flex: 1,
        renderCell: row => row.row.resourceName,
      },
    ],
    [t],
  )

  return (
    <CommonPageLayout
      titleSection={{ title: t('timeReportManagement') }}
      topAction={[
        <TotalTimeBox key="summary-count" theme={theme}>
          {t('entries')}: {summary.count}
        </TotalTimeBox>,
        <TotalTimeBox key="summary-total" theme={theme}>
          {t('totalTime')}: {summary.totalTime}
        </TotalTimeBox>,
        <TotalTimeBox key="summary-resources" theme={theme}>
          {t('resources', { ns: 'task' })}: {summary.uniqueResources}
        </TotalTimeBox>,
      ]}
    >
      <TimeReportFilter key="filter-actions" onChange={timeReportFilterChange} />
      <Box padding={'8px'} bgcolor={getThemeColor(theme, MUIThemeColors.white)} borderRadius={'4px'}>
        <GenericTable
          queryUrl={timeReportsEndpoints.filterTimeReport(customer.id)}
          queryParams={queryParams}
          columns={STATIC_COLUMNS}
          enableServerPagination
          id={'filtered-time-reports'}
          autoHeight={false}
          height={window.innerHeight - 300 + 'px'}
          getRowHeight={() => 'auto'}
        />
      </Box>
    </CommonPageLayout>
  )
}

export default TimeReports
